import React, { useEffect, useState, useCallback } from 'react';
import api, { formatRupiah } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';

const EMOJI_OPTIONS = ['☕', '🍜', '🍕', '🍩', '🧋', '🥤', '🍎', '🍰', '📚', '💖', '⚡', '🎁'];

export default function DashUnits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', emoji: '☕', price: 10000, description: '' });

  const load = useCallback(async () => {
    try { const res = await api.get('/units'); setRows(res.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    setEditing('new');
    setForm({ name: '', emoji: '☕', price: 10000, description: '' });
  };
  const startEdit = (u) => {
    setEditing(u.unit_id);
    setForm({ name: u.name, emoji: u.emoji || '☕', price: u.price, description: u.description || '' });
  };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.price < 1000) {
      toast.error('Nama harus diisi dan harga min Rp 1.000');
      return;
    }
    try {
      if (editing === 'new') {
        await api.post('/units', form);
        toast.success('Unit ditambahkan');
      } else {
        await api.put(`/units/${editing}`, form);
        toast.success('Unit diperbarui');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Hapus unit ini?')) return;
    try { await api.delete(`/units/${id}`); toast.success('Unit dihapus'); load(); }
    catch { toast.error('Gagal menghapus'); }
  };

  return (
    <div className="space-y-6" data-testid="dash-units">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-1">Unit Traktiran</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Atur Unitmu</h1>
        </div>
        {!editing && (
          <button onClick={startAdd}
            className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-4 py-2 font-bold uppercase tracking-wide shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
            data-testid="add-unit-btn">
            <Plus size={16} weight="bold" /> Tambah Unit
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6 space-y-4" data-testid="unit-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Nama Unit</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
                placeholder="Kopi" required data-testid="unit-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Harga (Rp)</label>
              <input type="number" min="1000" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value || '0') })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
                required data-testid="unit-price-input" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Emoji</label>
            <div className="flex flex-wrap gap-2">
              {EMOJI_OPTIONS.map((e) => (
                <button type="button" key={e} onClick={() => setForm({ ...form, emoji: e })}
                  className={`w-12 h-12 border-2 border-foreground text-2xl flex items-center justify-center transition-all ${
                    form.emoji === e ? 'bg-primary shadow-none translate-x-[2px] translate-y-[2px]' : 'bg-white shadow-brutal-sm'
                  }`}
                  data-testid={`unit-emoji-${e}`}>
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Deskripsi (opsional)</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              data-testid="unit-desc-input" />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="border-2 border-foreground bg-primary text-white px-5 py-2.5 font-bold uppercase tracking-wide shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]" data-testid="unit-save-btn">
              Simpan
            </button>
            <button type="button" onClick={cancel} className="border-2 border-foreground bg-white px-5 py-2.5 font-bold uppercase tracking-wide shadow-brutal-sm" data-testid="unit-cancel-btn">
              Batal
            </button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-full">Memuat...</div>
        ) : rows.length === 0 ? (
          <div className="col-span-full border-2 border-dashed border-foreground p-8 text-center text-muted-foreground">
            Belum ada unit. Tambah unit pertamamu untuk mulai terima traktiran.
          </div>
        ) : (
          rows.map((u) => (
            <div key={u.unit_id} className="border-2 border-foreground bg-white shadow-brutal-sm p-5 flex items-center gap-4" data-testid={`unit-card-${u.unit_id}`}>
              <div className="text-4xl">{u.emoji}</div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl font-bold">{u.name}</p>
                <p className="font-bold text-primary">{formatRupiah(u.price)}</p>
                {u.description && <p className="text-sm text-muted-foreground truncate">{u.description}</p>}
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => startEdit(u)} className="w-9 h-9 border-2 border-foreground bg-accent flex items-center justify-center shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]" data-testid={`unit-edit-${u.unit_id}`}>
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button onClick={() => remove(u.unit_id)} className="w-9 h-9 border-2 border-foreground bg-destructive text-white flex items-center justify-center shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]" data-testid={`unit-delete-${u.unit_id}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
